import { ReactNode } from "react";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Combobox, ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboboxLookup } from "@/Common/Components/Inputs/ComboboxLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./DosageEditForm.module.scss";

import img_icon_delete from "@/Globals/_images/icons/delete.png";

interface IDosageEditFormProps {
    children?: ReactNode;
    medicationModelModelId: string;
    currentDosageId: string;
    currentDosageString: string;
    currentDosageFrequency: string;
    currentDosageIfNeeded: boolean;
    currentDosageServedFrequency: string;
    currentDosageServedDays: string;
    isNarcotic: boolean;
    numberOfDays: number;
    isFirstDosage: boolean;
    isLastDosage: boolean;
    isFakeDosage: boolean;
    selectedDosageTypeId: string;
    selectedDosageUnitId: string;
    dosageTypes: Immutable.List<FlatLookupView>;
    frequencies: Immutable.List<ComboBoxDropDownItem>;
    dosageUnits: Immutable.List<ComboBoxDropDownItem>;
    dosageStringTextboxCss?: string;
    onDosageStringChange: (newText: string) => void;
    onDosageUnitChange: (selectedCode: string) => void;
    onDosageTypeChange: (selectedLookupId: string) => void;
    onDosageFrequencyChange: (selectedCode: string) => void;
    onDosageIfNeededChange: (isChecked: boolean, id: string, target?: number) => void;
    onDosageServedFrequencyChange: (newText: string) => void;
    onDosageServedDaysChange: (newText: string) => void;
    onDeleteDosageClick: () => void;
}

export function DosageEditForm(props: IDosageEditFormProps): JSX.Element {
    const { getText } = useTranslation();

    // Fix Default Values when null
    const id = props.medicationModelModelId + props.currentDosageId;

    return (
        <div key={"dosage_" + id}>
            {!props.isFirstDosage && (
                <div>
                    <div>
                        <span className={styles.followedByLabel}>{getText("FollowedByLabel") + ": "}</span>
                    </div>
                </div>
            )}

            {!props.isFakeDosage && (
                <div className="flex-container meds">
                    <div className="label">
                        <span>{getText("DosageLabel")}</span>
                    </div>
                    <div className="editablecell">
                        <TextboxControl
                            IsSmall={true}
                            className={`form-control input ${styles.dosageStringTextbox} ${props.dosageStringTextboxCss ?? ""}`}
                            value={props.currentDosageString}
                            id={"_dosestring" + props.currentDosageId}
                            onChange={(newText: string) => props.onDosageStringChange(newText)}
                            dataCy="dosageEditForm-dosage"
                        />
                    </div>
                    <div className={`editablecell ${styles.dosageUnitContainer}`}>
                        <Combobox
                            id="_unitCombobox"
                            className="sm"
                            selectedCode={props.selectedDosageUnitId}
                            items={props.dosageUnits}
                            onChange={(selectedCode: string) => props.onDosageUnitChange(selectedCode)}
                            HasEmptyItem={true}
                            doInitialChangeOp={false}
                            dataCy="dosageEditForm-unit"
                        />
                    </div>
                    <div className="editablecell">
                        <ComboboxLookup
                            id="_dosageTypeCombobox"
                            className="sm"
                            selectedItemLookupId={props.selectedDosageTypeId}
                            itemsLookup={props.dosageTypes}
                            onChange={selectedLookupId => props.onDosageTypeChange(selectedLookupId)}
                        />
                    </div>
                    <div
                        className="editablecell"
                        onClick={() => props.onDeleteDosageClick()}
                    >
                        <img
                            alt="delete dosage"
                            className={`file-img ${styles.deleteIcon}`}
                            src={img_icon_delete}
                            aria-expanded="false"
                        />
                    </div>
                </div>
            )}

            {!props.isFakeDosage && (
                <div className="flex-container meds">
                    <div className="label">
                        <span>{getText("FrequencyLabel")}</span>
                    </div>
                    <div className={`editablecell ${styles.dosageFrequencyComboboxContainer}`}>
                        <Combobox
                            id="_frequencyCombobox"
                            className="sm"
                            includeCodeInName={false}
                            selectedCode={props.currentDosageFrequency}
                            items={props.frequencies}
                            onChange={(selectedCode: string) => props.onDosageFrequencyChange(selectedCode)}
                            HasEmptyItem={true}
                            doInitialChangeOp={false}
                            dataCy="dosageEditForm-frequency"
                        />
                    </div>
                    <div className="editablecell">
                        <CheckboxControl
                            id={"ifneeded" + id}
                            checked={props.currentDosageIfNeeded}
                            label={getText("OnlyIfNeededLabel")}
                            onChange={(isChecked: boolean, id: string, target?: number) => props.onDosageIfNeededChange(isChecked, id, target)}
                        />
                    </div>
                </div>
            )}
            {props.children}
            {props.isNarcotic && (
                <div className={`flex-container meds ${styles.servedDosageContainer}`}>
                    <div className="label"><span /></div>
                    <div className={`editablecell ${styles.dosageServedFrequency}`}>
                        <TextboxControl
                            IsSmall={true}
                            IsNumeric={true}
                            className={`form-control input ${styles.dosageServedFrequency}`}
                            value={props.currentDosageServedFrequency}
                            id={"_doseServedFrequency" + id}
                            onChange={(newText: string) => props.onDosageServedFrequencyChange(newText)}
                        />
                    </div>
                    <div className={`editablecell ${styles.servedEveryLabelContainer}`}>
                        <span>{getText("AtATimeLabel") + ", " + getText("ServedEveryLabel").toLowerCase()}</span>
                    </div>
                    <div className={`editablecell ${styles.dosageServedDays}`}>
                        <TextboxControl
                            IsSmall={true}
                            IsNumeric={true}
                            className={`form-control input ${styles.dosageServedDays}`}
                            value={props.currentDosageServedDays}
                            id={"_doseServedDays" + id}
                            onChange={(newText: string) => props.onDosageServedDaysChange(newText)}
                        />
                    </div>
                    <div className={`editablecell ${styles.daysLabel}`}>
                        <span>{getText("DaysLabel")}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
